import {
    getAttendeeTimezone,
    shouldShowDateInAttendeeTimezone,
} from '@eventbrite/event-date-time';
import { gettext } from '@eventbrite/i18n';
import { CalendarCheckmarkIcon } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { LISTING_HEAP_IDS, STATSIG_EVENT_NAMES } from '../../../../constants';
import { isEventEligibleForRepeatingEventsConsumerExperiment } from '../../../../statsig';
import type {
    ChildEventProps,
    DateAndTimeScopedProps,
    Event,
    LocalizedDateTime,
} from '../../../../types';
import {
    Detail,
    DetailContent,
    DetailInner,
} from '../../../_shared/Detail/Detail';

import {
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../../EventDetails';
import { ChildEventsSlider } from '../ChildEventsSelection/ChildEventsSlider';
import {
    eventIsAvailable,
    findEventOnAvailableEvents,
    findNextAvailableEvent,
    shouldShowMoreOptions,
} from './utils';

import './DateAndTime.scss';

import { DisplayDate } from './DisplayDate';

export type DateAndTimeProps = DateAndTimeScopedProps & {
    locale: string;
    event: Pick<
        Event,
        | 'id'
        | 'start'
        | 'end'
        | 'hideStartDate'
        | 'hideEndDate'
        | 'childEvents'
        | 'isOnlineEvent'
    > & {
        listingDisplayFlags: {
            shouldShowTimezone: boolean;
        };
    };
    isSeriesParent: boolean;
    shouldDisplayCrawlableSeriesChildrenLinks: boolean;
};

type MoreOptions = {
    onButtonClick: () => void;
};

export const DateAndTime = ({
    event,
    displayDate,
    shouldShowDoorTime,
    doorTime,
    locale,
    isSeriesParent,
    shouldDisplayCrawlableSeriesChildrenLinks,
}: DateAndTimeProps) => {
    const { id: eventId, childEvents, isOnlineEvent } = event;
    const eventToShow = isSeriesParent && childEvents ? childEvents[0] : event;
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);

    const [isExperimentActive, setIsExperimentActive] =
        useState<Boolean | null>(null);

    const availableChildEvents = childEvents
        ? childEvents.filter(eventIsAvailable)
        : [];

    const LABEL_ID = 'date-time-heading';

    const shouldShowChildEvents = childEvents && childEvents.length > 0;

    const userTimezone = getAttendeeTimezone();
    const shouldShowChildEventsInAttendeeTimeZone =
        shouldShowDateInAttendeeTimezone({ isOnlineEvent, userTimezone });

    const getChildEventsInAttendeeTimeZone = (
        childEvents: ChildEventProps[],
    ): Array<ChildEventProps> => {
        const childEventsInAttendeeTimeZone: Array<ChildEventProps> = [];

        childEvents?.forEach((childEvent) => {
            const formatedDate = moment
                .tz(childEvent.start.utc, userTimezone)
                .format('YYYY-MM-DDTHH:mm:ss');

            const childEventInAttendeeTimeZone: ChildEventProps = {
                ...childEvent,
                start: {
                    ...childEvent.start,
                    local: formatedDate,
                    timezone: userTimezone as string,
                },
            };
            childEventsInAttendeeTimeZone.push(childEventInAttendeeTimeZone);
        });

        return childEventsInAttendeeTimeZone;
    };

    const eventStartInAttendeeTimeZone = {
        local: moment
            .tz(event.start.utc, userTimezone)
            .format('YYYY-MM-DDTHH:mm:ss'),
        timezone: userTimezone,
        utc: event.start.utc,
    };

    const LazyMoreOptionsModal = loadable(
        () =>
            import('../ChildEventsSelection/MoreOptionsModal/MoreOptionsModal'),
    );

    const handleMoreOptionsClick = () => {
        setMoreOptionsModalOpened(true);
    };

    const MORE_OPTIONS = gettext('More options');

    const MoreOptionsButton = ({ onButtonClick }: MoreOptions) => (
        <div className="more-options-button">
            <button
                className="eds-btn eds-btn--link"
                data-heap-id={LISTING_HEAP_IDS.MORE_OPTIONS_BUTTON}
                data-testid="more-options-button-mobile"
                onClick={() => {
                    onButtonClick();
                    logEvent(STATSIG_EVENT_NAMES.MORE_OPTIONS_BUTTON);
                }}
            >
                {MORE_OPTIONS}
            </button>
        </div>
    );

    const availableEventToShow = isSeriesParent
        ? availableChildEvents[0]
        : event;

    useEffect(() => {
        if (isExperimentActive === null) {
            const experimentValue =
                isEventEligibleForRepeatingEventsConsumerExperiment(
                    isSeriesParent,
                    childEvents,
                );
            setIsExperimentActive(experimentValue);
        }
    }, [isSeriesParent, childEvents, isExperimentActive]);

    const shouldNotRenderDateAndTime =
        !displayDate ||
        (isSeriesParent &&
            !shouldDisplayCrawlableSeriesChildrenLinks &&
            !isExperimentActive) ||
        (isSeriesParent && availableChildEvents.length < 1);

    if (shouldNotRenderDateAndTime) {
        return null;
    }

    const selectedEvent =
        findEventOnAvailableEvents(
            availableEventToShow,
            availableChildEvents,
        ) ?? findNextAvailableEvent(availableEventToShow, availableChildEvents);

    return (
        <>
            <EventDetailsSection aria-labelledby={LABEL_ID}>
                <Detail>
                    <DetailInner>
                        <DetailContent>
                            <EventDetailsSectionTitle>
                                {shouldShowChildEvents ? (
                                    <h2>{gettext('Select date and time')}</h2>
                                ) : (
                                    <h2>{gettext('Date and time')}</h2>
                                )}
                            </EventDetailsSectionTitle>
                            <div
                                className={classNames({
                                    'select-date-and-time':
                                        shouldShowChildEvents,
                                })}
                            >
                                <div className="date-info">
                                    <CalendarCheckmarkIcon className="date-info__icon" />
                                    <div>
                                        <DisplayDate
                                            doorTime={doorTime}
                                            eventToShow={eventToShow}
                                            isOnlineEvent={isOnlineEvent}
                                            locale={locale}
                                            shouldShowDoorTime={
                                                shouldShowDoorTime
                                            }
                                        />
                                        {childEvents &&
                                            shouldShowMoreOptions(
                                                childEvents,
                                            ) && (
                                                <MoreOptionsButton
                                                    onButtonClick={
                                                        handleMoreOptionsClick
                                                    }
                                                />
                                            )}
                                    </div>
                                </div>
                                {shouldShowChildEvents && (
                                    <ChildEventsSlider
                                        childEvents={
                                            shouldShowChildEventsInAttendeeTimeZone
                                                ? getChildEventsInAttendeeTimeZone(
                                                      availableChildEvents,
                                                  )
                                                : availableChildEvents
                                        }
                                        eventId={eventId}
                                        currentEventStartDate={
                                            shouldShowChildEventsInAttendeeTimeZone
                                                ? (eventStartInAttendeeTimeZone as LocalizedDateTime)
                                                : selectedEvent.start
                                        }
                                        currentEventId={selectedEvent.id}
                                    />
                                )}
                            </div>
                        </DetailContent>
                    </DetailInner>
                </Detail>
                {moreOptionsModalOpened && (
                    <LazyMoreOptionsModal
                        onClose={() => setMoreOptionsModalOpened(false)}
                        opened={moreOptionsModalOpened}
                        childEvents={childEvents}
                        currentEventStartDate={selectedEvent.start}
                        currentEventId={selectedEvent.id}
                    />
                )}
            </EventDetailsSection>
        </>
    );
};
